import React, { useCallback, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Box, Fab, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { tlenv } from 'tldraw';
import { isPaidPlan } from '../../utils/plans';
import BoardChatPanel from './BoardChatPanel';
import { useBoardConversation } from '../hooks/useBoardConversation';
import { useBoardAiContext } from '../context/BoardAiContext';
import { useChatModel } from '../hooks/useChatModel';
import { useGeminiLanguage } from '../hooks/useGeminiLanguage';
import { getCanvasMessages } from '../utils/canvasI18n';
import {
  buildConversationPinPayload,
  buildExchangePinPayload,
  createConversationPinShape,
} from '../utils/createConversationPin';
import { placeSuggestPinFromSource } from '../utils/applyBoardAiActions';
import { placeChatArtifactOnCanvas } from '../utils/placeChatArtifact';
import { publishPinnedConversation } from '../api/boardApi';

const ASK_AI_SHORTCUT_LABEL = tlenv.isDarwin ? '⌘J' : 'Ctrl+J';

function isAskAiShortcut(event) {
  if (event.repeat || event.shiftKey || event.altKey) return false;
  if (event.code !== 'KeyJ') return false;
  if (tlenv.isDarwin) return event.metaKey && !event.ctrlKey;
  return event.ctrlKey && !event.metaKey;
}

function isBlockingOverlayOpen() {
  return Boolean(document.querySelector(
    '.MuiDialog-root, .tlui-dialog__overlay, .tlui-menu, [aria-modal="true"]'
  ));
}

const BoardChatLauncher = ({
  hashtag,
  editorRef,
  board,
  activities,
  updatePlacementsLocal,
  savePlacements,
  saveFrames,
  saveSnapshot,
  deleteActivities,
  analyzePinAdded,
}) => {
  const {
    registerOpenConversation,
    registerOpenBoardChat,
    registerStartChatWithMessage,
  } = useBoardAiContext();
  const user = useSelector(state => state.user);
  const canUsePremiumChatModels = isPaidPlan(user?.subscriptionStatus);
  const { chatModel, setChatModel } = useChatModel();
  const effectiveChatModel = canUsePremiumChatModels || chatModel === 'fast' ? chatModel : 'fast';
  const { geminiLanguage } = useGeminiLanguage();
  const messages = useMemo(() => getCanvasMessages(geminiLanguage), [geminiLanguage]);
  const chatModelOptions = useMemo(() => ([
    {
      id: 'fast',
      label: messages.chatModelFast,
      detail: messages.chatModelFastDetail,
    },
    {
      id: 'better',
      label: messages.chatModelBetter,
      detail: canUsePremiumChatModels ? messages.chatModelBetterDetail : messages.chatModelPaidOnly,
      disabled: !canUsePremiumChatModels,
    },
    {
      id: 'pro',
      label: messages.chatModelPro,
      detail: canUsePremiumChatModels ? messages.chatModelProDetail : messages.chatModelPaidOnly,
      disabled: !canUsePremiumChatModels,
    },
  ]), [messages, canUsePremiumChatModels]);
  const chat = useBoardConversation({
    hashtag,
    editorRef,
    board,
    activities,
    updatePlacementsLocal,
    savePlacements,
    saveFrames,
    saveSnapshot,
    deleteActivities,
    enabled: true,
    chatModel: effectiveChatModel,
  });

  useEffect(() => {
    registerOpenConversation?.(chat.openConversation);
    return () => registerOpenConversation?.(null);
  }, [registerOpenConversation, chat.openConversation]);

  useEffect(() => {
    registerOpenBoardChat?.(chat.openNewChat);
    return () => registerOpenBoardChat?.(null);
  }, [registerOpenBoardChat, chat.openNewChat]);

  useEffect(() => {
    registerStartChatWithMessage?.(chat.startChatWithMessage);
    return () => registerStartChatWithMessage?.(null);
  }, [registerStartChatWithMessage, chat.startChatWithMessage]);

  useEffect(() => {
    const onAskAiShortcut = event => {
      if (!isAskAiShortcut(event) || isBlockingOverlayOpen()) return;
      event.preventDefault();
      event.stopPropagation();
      if (chat.open) {
        chat.setOpen(false);
        return;
      }
      chat.openNewChat();
    };

    const onEscape = event => {
      if (event.key !== 'Escape' || event.defaultPrevented || !chat.open) return;
      if (isBlockingOverlayOpen()) return;
      if (document.querySelector('.MuiMenu-root, .MuiPopover-root, .MuiModal-root')) return;
      const target = event.target;
      if (
        target instanceof Element
        && target.closest('.tl-container')
        && !target.closest('.board-chat-panel')
      ) {
        return;
      }
      chat.setOpen(false);
    };

    window.addEventListener('keydown', onAskAiShortcut, true);
    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('keydown', onAskAiShortcut, true);
      window.removeEventListener('keydown', onEscape);
    };
  }, [chat.open, chat.setOpen, chat.openNewChat]);

  const handlePinConversation = useCallback(() => {
    const editor = editorRef.current;
    const conversation = chat.activeConversation;
    if (!editor || !conversation) return;
    const payload = buildConversationPinPayload(conversation);
    if (!payload) return;
    const shapeId = createConversationPinShape(editor, payload, { saveSnapshot });
    publishPinnedConversation(hashtag, conversation, { pinKind: 'conversation' }).catch(err => {
      console.warn('Failed to publish pinned conversation', err);
    });
    if (!shapeId) return;
    analyzePinAdded?.({
      aiPin: {
        id: shapeId,
        shapeId,
        title: payload.title || conversation.title || '',
        query: '',
        question: payload.snippet || '',
        reason: 'conversation-pin',
      },
      placePending: false,
    });
  }, [editorRef, chat.activeConversation, saveSnapshot, analyzePinAdded, hashtag]);

  const handlePinExchange = useCallback(assistantMessageIndex => {
    const editor = editorRef.current;
    const conversation = chat.activeConversation;
    if (!editor || !conversation) return;
    const payload = buildExchangePinPayload(conversation, assistantMessageIndex);
    if (!payload) return;
    const shapeId = createConversationPinShape(editor, payload, { saveSnapshot });
    publishPinnedConversation(hashtag, conversation, {
      pinKind: 'exchange',
      messageAt: payload.messageAt,
    }).catch(err => {
      console.warn('Failed to publish pinned exchange', err);
    });
    if (!shapeId) return;
    analyzePinAdded?.({
      aiPin: {
        id: shapeId,
        shapeId,
        title: payload.title || '',
        query: '',
        question: payload.snippet || payload.assistantText || '',
        reason: 'conversation-exchange-pin',
      },
      placePending: false,
    });
  }, [editorRef, chat.activeConversation, saveSnapshot, analyzePinAdded, hashtag]);

  const handlePinSource = useCallback(source => {
    const editor = editorRef.current;
    if (!editor || !source?.url) return false;
    const shapeId = placeSuggestPinFromSource(editor, source, { saveSnapshot });
    return Boolean(shapeId);
  }, [editorRef, saveSnapshot]);

  const handlePlaceArtifact = useCallback(artifact => {
    const editor = editorRef.current;
    if (!editor || !artifact) return false;
    const shapeId = placeChatArtifactOnCanvas(editor, artifact, { saveSnapshot });
    return Boolean(shapeId);
  }, [editorRef, saveSnapshot]);

  return createPortal(
    <>
      {!chat.open ? (
        <Tooltip title={`Ask AI · ${ASK_AI_SHORTCUT_LABEL}`} arrow placement="left">
          <Fab
            className="board-chat-fab"
            onClick={() => {
              chat.openNewChat();
            }}
            aria-label={`Open Ask AI board chat (${ASK_AI_SHORTCUT_LABEL})`}
          >
            <Box className="board-chat-fab-inner">
              <AutoAwesomeIcon className="board-chat-fab-icon" />
            </Box>
          </Fab>
        </Tooltip>
      ) : null}
      <BoardChatPanel
        open={chat.open}
        onClose={() => chat.setOpen(false)}
        conversations={chat.conversations}
        activeConversationId={chat.activeConversationId}
        onSelectConversation={chat.setActiveConversationId}
        onNewConversation={chat.startNewConversation}
        onDeleteConversation={chat.removeConversation}
        messages={chat.messages}
        input={chat.input}
        onInputChange={chat.setInput}
        onSend={chat.sendMessage}
        inAppAskLabel={messages.askInApp}
        chatModel={effectiveChatModel}
        chatModelOptions={chatModelOptions}
        onChatModelChange={nextModel => {
          if (!canUsePremiumChatModels && nextModel !== 'fast') return;
          setChatModel(nextModel);
        }}
        chatModelMenuLabel={messages.chatModelMenuLabel}
        loading={chat.loading}
        bootstrapping={chat.bootstrapping}
        creatingConversation={chat.creatingConversation}
        error={chat.error}
        thinkingPhase={chat.thinkingPhase}
        lastJobMetrics={chat.lastJobMetrics}
        composerContextItems={chat.composerContextItems}
        onRemoveComposerContextItem={chat.removeComposerContextItem}
        pinLabelById={chat.pinLabelById}
        onPinConversation={handlePinConversation}
        onPinExchange={handlePinExchange}
        onPinSource={handlePinSource}
        onPlaceArtifact={handlePlaceArtifact}
        focusMessageAt={chat.focusMessageAt}
        onFocusMessageHandled={chat.clearFocusMessage}
      />
    </>,
    document.body
  );
};

export default BoardChatLauncher;
